import { useState, useCallback } from 'react';
import { useRouter } from 'next/router';
import { API_CONSTANTS, PAYMENT_EXPIRY_TIME } from '../../utils';
import { useHandleApiCall } from '../useHandleApiCall';
import { useGetSubscriptionPlans } from '../subscription-plans/useGetSubscriptionPlans';
import { useSnackbar } from 'notistack';
import { useTheme, useMediaQuery } from '@mui/material';
import { readClickIds } from '../../utils/marketing/allAdsPlatformTracking';

export const usePaymentHandler = (
  showSpecificPlan = '',
  lifetimeVisibility?: 'all' | 'owned' | 'none'
) => {
  console.log('usePaymentHandler called with plan:', showSpecificPlan || '<all>');
  const { push, query, back } = useRouter();
  const videoId = query?.videoId as string;
  const from = query?.from;

  const { get, post } = useHandleApiCall();
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<{ clientSecret: string; paymentId: string } | null>(null);
  const [isAssigningCredit, setIsAssigningCredit] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<{
    id: string;
    amount: number;
    discount: number;
    interval: string;
    name: string;
  } | null>(null);
  const [showCheckoutOption, setShowCheckoutOption] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { data: subscriptionPlans, isLoading } = useGetSubscriptionPlans(
    showSpecificPlan,
    lifetimeVisibility
  );

  const { mutateAsync: createPaymentMutation, isLoading: createPaymentMutationIsLoading } = post(
    API_CONSTANTS.CREATE_PAYMENT.Key,
    API_CONSTANTS.CREATE_PAYMENT.path
  );

  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const getPaymentId = useCallback(
    async (id: string, affiliateId: string) => {
      const data = await createPaymentMutation({
        planId: id,
        session_expiry: PAYMENT_EXPIRY_TIME,
        affiliateId,
      });
      return { clientSecret: data.client_secret, paymentId: data.payment_id };
    },
    [createPaymentMutation]
  );

  const handleBuy = useCallback(
    async (planOverride?: { id: string; amount: number; discount: number; interval: string }) => {
      try {
        const planToBuy = planOverride ?? selectedPlan;
        if (!planToBuy) return;
        const trackingData = readClickIds(); // Get stored tracking data
        const trackingValue = trackingData || null; // Use value or null
        const { clientSecret, paymentId } = await getPaymentId(planToBuy.id, trackingValue);
        setOptions({ clientSecret, paymentId });
        setShowCheckoutOption(true);
      } catch (err) {
        console.error(err);
        if (err?.response?.data?.message?.includes('User already has an'))
          enqueueSnackbar('You already have an active subscription for this plan', {
            variant: 'error',
          });
        else if (err?.response?.data?.statusCode === 403) {
          enqueueSnackbar(err?.response?.data?.message, { variant: 'warning' });
        } else {
          enqueueSnackbar('We hit a glitch in the matrix! Please try again.', { variant: 'error' });
        }
      }
    },
    [selectedPlan, getPaymentId]
  );

  const handleBuyWithDefaultMethod = useCallback(
    async (planOverride?: { id: string; amount: number; discount: number; interval: string }) => {
      try {
        const planToBuy = planOverride ?? selectedPlan;
        if (!planToBuy) return false;
        const trackingData = readClickIds();
        const trackingValue = trackingData || null;

        const data = await createPaymentMutation({
          planId: planToBuy.id,
          session_expiry: PAYMENT_EXPIRY_TIME,
          affiliateId: trackingValue,
          useDefaultPaymentMethod: true,
        });

        // Keep latest payment id available for downstream credit assignment flow.
        if (data?.payment_id) {
          setOptions({ clientSecret: data.client_secret || '', paymentId: data.payment_id });
        }
        setShowCheckoutOption(false);
        return true;
      } catch (err) {
        return false;
      }
    },
    [selectedPlan, createPaymentMutation]
  );

  const handleSuccess = useCallback(() => {
    setIsOpen(false);
    setIsAssigningCredit(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setShowCheckoutOption(false);
    setOptions(null);
  }, []);

  const handleClose = useCallback(() => {
    setIsAssigningCredit(false);

    try {
      if (from === 'upgrade') {
        const generateMediaData = localStorage.getItem('generateMedia');
        const generateIMediaData = localStorage.getItem('generateIMedia');

        if (generateMediaData || generateIMediaData) {
          const data = JSON.parse(generateMediaData || generateIMediaData);
          if (data?.url) {
            data.complete = true;
            if (generateMediaData) {
              localStorage.setItem('generateMedia', JSON.stringify(data));
            }
            if (generateIMediaData) {
              localStorage.setItem('generateIMedia', JSON.stringify(data));
            }
            push(data.url + '?from=upgrade');
          }
        }
      }
    } catch (err) {
      console.log(err);
    }

    try {
      if(from === 'agent_upsell') {
        push('/ai-studio');
        return;
      }
    } catch (error) {
      console.error('Error handling agent upsell redirect:', error);
    }

    if (videoId) {
      push(`/create-video-details?draftVideoId=${videoId}`);
    } else if (from) {
      back();
    }
    // else if (isExtraSmall) {
    //   push(`/broll`);
    // }
    else {
      // push(`/dashboard`);
      push(`/text-to-image`);
    }
  }, [videoId, from, push, back]);

  return {
    isOpen,
    setIsOpen,
    isExtraSmall,
    options,
    isAssigningCredit,
    selectedPlan,
    setSelectedPlan,
    showCheckoutOption,
    subscriptionPlans,
    isLoading,
    createPaymentMutationIsLoading,
    handleBuy,
    handleBuyWithDefaultMethod,
    handleSuccess,
    handleCancel,
    handleClose,
  };
};
